import React, { useState, useEffect } from "react";
import Select from "react-select";
import { industryMap } from "../../utils/industryMap";
import {itemsToExclude} from "../../utils/mappings";

const CraftingCalculator = ({ items }) => {
  // Load initial state from localStorage or use defaults
  const [state, setState] = useState(() => {
    const saved = localStorage.getItem('craftingCalculatorState');
    return saved ? JSON.parse(saved) : {
      ownedMaterials: {},
      projects: [
        {
          skill: null,
          recipe: null,
          amount: 1,
          machines: 1,
          exp: 0,
        }
      ]
    };
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('craftingCalculatorState', JSON.stringify(state));
  }, [state]);

  const addProject = () => {
    setState(prev => ({
      ...prev,
      projects: [
        ...prev.projects,
        {
          skill: null,
          recipe: null,
          amount: 1,
          machines: 1,
          exp: 0,
        }
      ]
    }));
  };

  const removeProject = (index) => {
    if (state.projects.length <= 1) return;
    setState(prev => {
      const newProjects = [...prev.projects];
      newProjects.splice(index, 1);
      return { ...prev, projects: newProjects };
    });
  };

  // Update a project's property
  const updateProject = (index, field, value) => {
    setState(prev => {
      const newProjects = [...prev.projects];

      if (field === "exp") {
        const expValue = parseInt(value) || 0;
        newProjects[index].exp = expValue;

        if (newProjects[index].recipe) {
          const batches = Math.max(1, Math.ceil(expValue / newProjects[index].recipe.exp));
          newProjects[index].amount = batches * newProjects[index].recipe.quantityProduced;
        }
      } else {
        newProjects[index][field] = value;

        if (field === "amount" && newProjects[index].recipe) {
          const batchesNeeded = Math.max(1, Math.ceil(value / newProjects[index].recipe.quantityProduced));
          newProjects[index].exp = batchesNeeded * newProjects[index].recipe.exp;
        }
      }

      return { ...prev, projects: newProjects };
    });
  };

  const calculateProjectMaterials = (project) => {
    if (!project.recipe) return [];

    const batchesNeeded = Math.ceil(project.amount / project.recipe.quantityProduced);

    return project.recipe.ingredients.map((ingredient) => ({
      ...ingredient,
      totalNeeded: ingredient.quantity * batchesNeeded,
      owned: state.ownedMaterials[ingredient.id]?.owned || 0,
      toBuy: Math.max(0, ingredient.quantity * batchesNeeded - (state.ownedMaterials[ingredient.id]?.owned || 0)),
      totalCost: ingredient.unitPrice * Math.max(0, ingredient.quantity * batchesNeeded - (state.ownedMaterials[ingredient.id]?.owned || 0)),
    }));
  };

  const calculateProjectStats = (project) => {
    if (!project.recipe) return null;

    const batchesNeeded = Math.ceil(project.amount / project.recipe.quantityProduced);
    const materials = calculateProjectMaterials(project);

    return {
      time: (project.recipe.craftingTime * batchesNeeded) / project.machines,
      energy: project.recipe.energy * batchesNeeded,
      materialCost: materials.reduce((sum, mat) => sum + mat.totalCost, 0),
      energyCost: project.recipe.energyCost * batchesNeeded,
      totalCost: (project.recipe.costLessEnergy * batchesNeeded) + (project.recipe.energyCost * batchesNeeded),
      mpPrice: project.recipe.baseMarketPrice,
      profit: project.recipe.baseMarketPrice - ((project.recipe.costLessEnergy * batchesNeeded) + (project.recipe.energyCost * batchesNeeded)),
      exp: project.exp,
      withoutEnergy: project.recipe.baseMarketPrice - ((project.recipe.costLessEnergy * batchesNeeded))
    };
  };

  // Filter recipes by selected skill
  const craftingSkills = Object.entries(industryMap)
    .filter(([_, skillData]) => skillData.type === "crafting")
    .map(([key, value]) => ({
      value: key,
      label: value.name,
    }));

  const getRecipesForSkill = (skillValue) => {
    if (!skillValue) return [];

    const allRecipes = [];
    items.forEach((item) => {
      if (item.recipes) {
        item.recipes.forEach((recipe) => {
          if (!itemsToExclude.includes(recipe.id) && recipe.expType === skillValue) {
            allRecipes.push({
              ...recipe,
              itemName: item.name,
              itemId: item.id,
              baseMarketPrice: item.baseMarketPrice
            });
          }
        });
      }
    });

    return allRecipes.sort((a, b) => a.level - b.level || a.exp - b.exp);
  };

  // Update owned material quantity
  const updateOwnedMaterial = (id, quantity) => {
    setState(prev => ({
      ...prev,
      ownedMaterials: {
        ...prev.ownedMaterials,
        [id]: {
          ...prev.ownedMaterials[id],
          owned: parseInt(quantity) || 0,
        },
      }
    }));
  };

  return (
    <div className="calculator-style">
      <h2 className="calculator-title">Crafting Calculator</h2>

      {/* Updated grid container with centered layout */}
      <div
        className={`flex flex-wrap justify-center gap-6 mb-4 ${
          state.projects.length === 1
            ? "lg:grid-cols-1 max-w-md mx-auto"
            : state.projects.length === 2
            ? "lg:grid-cols-2 max-w-5xl mx-auto"
            : "lg:grid-cols-3 max-w-7xl mx-auto"
        }`}
      >
        {state.projects.map((project, projectIndex) => (
          <div
            key={projectIndex}
            className={`calculator-project ${
              state.projects.length === 1 ? "w-full" : 
              state.projects.length === 2 ? "w-full md:w-[calc(50%-1.5rem)]" : 
              "w-full md:w-[calc(33.333%-1.5rem)]"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Project #{projectIndex + 1}</h3>
              {state.projects.length > 1 && (
                <button onClick={() => removeProject(projectIndex)} className="calculator-remove">
                  Remove
                </button>
              )}
            </div>

            <div className="space-y-4">
              <div className="form-group">
                <label className="block mb-1 font-medium">Skill</label>
                <Select
                  options={craftingSkills}
                  value={project.skill}
                  onChange={(option) => {
                    updateProject(projectIndex, "skill", option);
                    updateProject(projectIndex, "recipe", null);
                  }}
                  placeholder="Select skill..."
                  className="calculator-form"
                />
              </div>

              <div className="form-group">
                <label className="block mb-1 font-medium">Recipe</label>
                <Select
                  options={getRecipesForSkill(project.skill?.label).map((recipe) => ({
                    value: recipe.achievementId,
                    label: (
                      <div className="flex items-center gap-2">
                        <img src={recipe.image} alt="" className="w-5 h-5" />
                        <span>
                          {recipe.name || recipe.itemName} (Lvl {recipe.level})
                        </span>
                      </div>
                    ),
                    data: recipe,
                  }))}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  value={
                    project.recipe
                      ? {
                          value: project.recipe.achievementId,
                          label: (
                            <div className="flex items-center gap-2">
                              <img src={project.recipe.image} alt="" className="w-5 h-5" />
                              <span>
                                {project.recipe.itemName || project.recipe.name} (Lvl {project.recipe.level})
                              </span>
                            </div>
                          ),
                          data: project.recipe
                        }
                      : null
                  }
                  onChange={(option) => {
                    updateProject(projectIndex, "recipe", option?.data);
                    if (option?.data) {
                      updateProject(projectIndex, "exp", option.data.exp * Math.ceil(project.amount / option.data.quantityProduced));
                    }
                  }}
                  placeholder="Select recipe..."
                  formatOptionLabel={(option) => option.label}
                  isDisabled={!project.skill}
                  className="calculator-form"
                />
              </div>

              {project.recipe && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="form-group">
                    <label className="block mb-1 text-xs font-semibold">EXP</label>
                    <input
                      type="number"
                      min="0"
                      value={project.exp}
                      onChange={(e) => updateProject(projectIndex, "exp", e.target.value)}
                      className="w-full p-2 border rounded calculator-form"
                    />
                  </div>

                  <div className="form-group">
                    <label className="block mb-1 text-xs font-semibold">Amount</label>
                    <input
                      type="number"
                      min="1"
                      value={project.amount}
                      onChange={(e) => updateProject(projectIndex, "amount", parseInt(e.target.value) || 1)}
                      className="w-full p-2 border rounded calculator-form"
                    />
                  </div>

                  <div className="form-group">
                    <label className="block mb-1 text-xs font-semibold">Machines</label>
                    <input
                      type="number"
                      min="1"
                      value={project.machines}
                      onChange={(e) => updateProject(projectIndex, "machines", parseInt(e.target.value) || 1)}
                      className="w-full p-2 border rounded calculator-form"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Materials for this project */}
            {project.recipe && (
              <div className="mt-6">
                <h4 className="font-medium mb-3">Materials Needed</h4>
                <div className="space-y-3">
                  {calculateProjectMaterials(project).map((material, index) => (
                    <div key={index} className="flex items-start gap-3 p-2 border rounded bg-[var(--entity-mini)] rounded-xl">
                      <img src={material.image} alt={material.name} className="h-10 w-10 object-contain rounded" />
                      <div className="flex-1">
                        <div className="font-medium">{material.name}</div>
                        <div className="flex justify-between text-xs">
                          <span>Needed: {material.totalNeeded}</span>
                          <span>Price: {material.totalCost.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs">You have:</span>
                          <input
                            type="number"
                            min="0"
                            value={material.owned}
                            onChange={(e) => updateOwnedMaterial(material.id, e.target.value)}
                            className="w-16 p-1 border rounded calculator-form"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Project Summary */}
                {project.recipe && (
                  <div className="mt-4 p-3 bg-[var(--entity-mini)] rounded-xl rounded border">
                    <h4 className="font-medium mb-2">Project Summary</h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>Time:</div>
                      <div className="font-medium">{calculateProjectStats(project).time.toFixed(2)} min</div>
                      
                      <div>EXP:</div>
                      <div className="font-medium">{calculateProjectStats(project).exp.toLocaleString()}</div>
 
                      <div>Energy:</div>
                      <div className="font-medium">{calculateProjectStats(project).energy}</div>

                      <div>Material Cost:</div>
                      <div className="font-medium">{calculateProjectStats(project).materialCost.toLocaleString()}</div>

                      <div>Energy Cost:</div>
                      <div className="font-medium">{calculateProjectStats(project).energyCost.toLocaleString()}</div>

                      <div>Total Cost:</div>
                      <div className="font-medium">{calculateProjectStats(project).totalCost.toLocaleString()}</div>
                     
                      <div>MP Price:</div>
                      <div className="font-medium">{calculateProjectStats(project).mpPrice?.toLocaleString() || "N/A"}</div>
                     
                      <div>Profit:</div>
                      <div className="font-medium">{calculateProjectStats(project).profit.toLocaleString()}</div>

                      <div>Profit w/Free Energy:</div>
                      <div className="font-medium">{calculateProjectStats(project).withoutEnergy.toLocaleString()}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <button onClick={addProject} className="panel-button">
        + Add Another Project
      </button>
    </div>
  );
};

export default CraftingCalculator;